import * as logClient from "@classdojo/log-client";
import { createPortal } from "react-dom";

import React, { createElement, lazy, Suspense, useState, useCallback, useEffect, useMemo } from "react";
import { useEventListener } from "@web-monorepo/hooks";
import { debounce } from "es-toolkit";
import type { TranslatedString } from "@web-monorepo/i18next";
import { Dock } from "./Dock";
import { useMediaQuery } from "react-responsive";
import { EventLogHistorySectionContainer } from "./EventLogHistorySection";
import { FeatureSwitchesSectionContainer } from "./FeatureSwitchesSection";
import { ReactQuerySection } from "./ReactQuerySection";
import { EnvironmentSection } from "./EnvironmentSection";

const LazyUserDataSection = lazy(() =>
  import("./UserDataSection").then((m) => ({ default: m.UserDataSectionContainer })),
);
const UserDataSectionContainer = () => (
  <Suspense fallback={null}>
    <LazyUserDataSection />
  </Suspense>
);
import { DevToolsSettings, PANEL_DISPLAY_SIDES, type PanelDisplaySide } from "./devToolsSettings";
import { IconOpenin, IconSettings, IconX } from "@web-monorepo/dds-icons";
import NewWindow from "react-new-window";
import {
  DDSBodyText,
  DDSButton,
  DDSHeadlineText,
  DDSTabContent,
  DDSTabs,
  DDSTabsList,
  DDSTabTrigger,
  DDSTooltip,
} from "@web-monorepo/dds";
// eslint-disable-next-line @web-monorepo/no-browser-storage -- using the safe wrapper, not raw localStorage
import { localStorage as safeStorage } from "@web-monorepo/safe-browser-storage";
import { keys } from "@web-monorepo/utilities/src/objects";
import styles from "./DevTools.module.css";
interface Section {
  title: string;
  component: React.ComponentType<{ windowObject?: typeof window }>;
}

// put the dock on top of the dev tools open button. Need to be careful not to
// hide nessie's popup menus
const DOCK_Z_INDEX = 201;

/**
 * Defines the dev tools sections that are available when the panel is open
 * `title` will be the text displayed on the section button and `component` is the
 * component used to display the section contents
 */
const DevToolbarSections = {
  FeatureSwitches: { title: "Feature Switches", component: FeatureSwitchesSectionContainer },
  EventLogHistory: { title: "Logged Events", component: EventLogHistorySectionContainer },
  UserData: { title: "User Data", component: UserDataSectionContainer },
  ReactQuery: { title: "React Query", component: ReactQuerySection },
  Environment: { title: "Environment", component: EnvironmentSection },
};

interface DevToolsProps {
  additionalSections?: Record<string, Section>;
}

/**
 * Renders a button to open the dev tools on the bottom right of the app
 * Dev tools are displayed by default in `dev` environment, but can always be
 * toggled using the query param `dev-tools=true` or `dev-tools=false`, even in
 * production environment.
 * Same thing can be done with localStorage, using an entry with `dev-tools=true`
 * or `dev-tools=false`
 */
export const DevTools = (props: DevToolsProps) => {
  // tracks if dev tools should be available or not
  const { isDevToolsEnabled: shouldShowDevTools } = DevToolsSettings.useIsDevToolsEnabled();

  return shouldShowDevTools ? <DevToolsInternal {...props} /> : null;
};

const DevToolsInternal = ({ additionalSections }: DevToolsProps) => {
  const incrementDevToolsOpenedMetric = useIncrementDevToolsOpenedMetric();

  const [isOpen, setIsOpen] = useState(false);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const openDevToolbar = useCallback(() => {
    setIsOpen(true);
    incrementDevToolsOpenedMetric();
  }, [incrementDevToolsOpenedMetric]);
  const closeDevToolbar = useCallback(() => {
    setIsOpen(false);
    setIsPoppedOut(false);
  }, []);
  const popOut = useCallback(() => {
    setIsPoppedOut(true);
  }, []);
  const popIn = useCallback(() => {
    setIsPoppedOut(false);
  }, []);

  const { panelDisplaySide, setPanelDisplaySide } = DevToolsSettings.usePanelDisplaySide();

  return (
    <>
      {createPortal(
        <>
          {!isOpen && !isPoppedOut && <OpenDevToolbarButton onClick={openDevToolbar} />}
          <DevToolbar
            isOpen={isOpen}
            isPoppedOut={isPoppedOut}
            closeDevToolbar={closeDevToolbar}
            popOut={popOut}
            popIn={popIn}
            panelDisplaySide={panelDisplaySide}
            setPanelDisplaySide={setPanelDisplaySide}
            additionalSections={additionalSections}
          />
        </>,
        document.body,
      )}
    </>
  );
};

interface OpenDevToolbarButtonProps {
  onClick: VoidFunction;
}

const OpenDevToolbarButton = ({ onClick }: OpenDevToolbarButtonProps) => {
  return (
    <div className={`${styles.openDevToolbarButton} ${styles.openDevToolbarButtonLeft}`}>
      <DDSTooltip content="Open dev tools" data-name="devTools.open.tooltip" side="right">
        <DDSButton
          onClick={onClick}
          data-name="devTools.open"
          variant="unstyled"
          className={styles.openDevToolbarIconButton}
          aria-label={"Open dev tools" as TranslatedString}
        >
          <IconSettings size="m" />
        </DDSButton>
      </DDSTooltip>
    </div>
  );
};

const DockPositionIndicator = ({
  position,
  isActive,
}: {
  position: "left" | "right" | "bottom";
  isActive: boolean;
}) => {
  const fill = isActive ? "var(--content-primary)" : "var(--content-secondary)";
  const bg = "var(--border-default)";
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
      <rect x="0.5" y="0.5" width="19" height="15" rx="2" stroke={bg} fill="none" />
      {position === "left" && <rect x="0.5" y="0.5" width="7" height="15" rx="2" fill={fill} />}
      {position === "right" && <rect x="12.5" y="0.5" width="7" height="15" rx="2" fill={fill} />}
      {position === "bottom" && <rect x="0.5" y="9.5" width="19" height="6" rx="2" fill={fill} />}
    </svg>
  );
};

const DockPositionPicker = ({
  current,
  onChange,
}: {
  current: PanelDisplaySide;
  onChange: (side: PanelDisplaySide) => void;
}) => {
  const positions: PanelDisplaySide[] = ["left", "bottom", "right"];
  return (
    <div className={styles.dockPositionPicker}>
      {positions.map((pos) => (
        <DDSButton
          key={pos}
          variant="unstyled"
          className={`${styles.dockPositionButton} ${current === pos ? styles.dockPositionButtonActive : ""}`}
          onClick={() => onChange(pos)}
          data-name={`dev_tools_dock_${pos}`}
          aria-label={`Dock ${pos}` as TranslatedString}
        >
          <DockPositionIndicator position={pos} isActive={current === pos} />
        </DDSButton>
      ))}
    </div>
  );
};

class DevToolsErrorBoundary extends React.Component<React.PropsWithChildren, { error: Error | null }> {
  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  state: { error: Error | null } = { error: null };

  render() {
    if (this.state.error) {
      return (
        <div className={styles.contentContainer} disable-tag-translation>
          <DDSBodyText variant="danger">{"Error rendering section:" as TranslatedString}</DDSBodyText>
          <DDSBodyText level="sm">{this.state.error.message as TranslatedString}</DDSBodyText>
          <DDSButton
            variant="secondary"
            data-name="dev_tools_error_retry"
            onClick={() => this.setState({ error: null })}
          >
            {"Retry" as TranslatedString}
          </DDSButton>
        </div>
      );
    }
    return this.props.children;
  }
}

const GITHUB_REPO_URL = "https://github.com/classdojo/web-monorepo";

function getVersionDisplay(): { label: string; url: string | null } {
  const appVersion = global.appVersion;
  if (
    !appVersion ||
    appVersion === "undefined" ||
    appVersion.includes("%") ||
    appVersion.includes("development") ||
    appVersion.includes("dev")
  ) {
    return { label: "Dev", url: null };
  }
  return {
    label: appVersion,
    url: `${GITHUB_REPO_URL}/releases/tag/production/${appVersion}`,
  };
}

const VersionInfo = () => {
  const { label, url } = getVersionDisplay();

  return (
    <div className={styles.versionInfo} disable-tag-translation>
      <DDSBodyText level="sm" disable-tag-translation>
        {"Version: " as TranslatedString}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className={styles.versionLink}>
            {label as TranslatedString}
          </a>
        ) : (
          <span>{label as TranslatedString}</span>
        )}
      </DDSBodyText>
    </div>
  );
};

interface DevToolbarProps {
  isOpen: boolean;
  isPoppedOut: boolean;
  closeDevToolbar: VoidFunction;
  popOut: VoidFunction;
  popIn: VoidFunction;
  panelDisplaySide: PanelDisplaySide;
  setPanelDisplaySide: (side: PanelDisplaySide) => void;
  additionalSections?: Record<string, Section>;
}

const DevToolbar = ({
  isOpen,
  isPoppedOut,
  closeDevToolbar,
  popOut,
  popIn,
  panelDisplaySide,
  setPanelDisplaySide,
  additionalSections,
}: DevToolbarProps) => {
  const allSections: Record<string, Section> = { ...DevToolbarSections, ...additionalSections };
  const [selectedSection, setSelectedSection] = DevToolsSettings.useSelectedSection("FeatureSwitches");

  const validSelectedSection = allSections[selectedSection] ? selectedSection : "FeatureSwitches";

  if (!isOpen && !isPoppedOut) return null;

  const toolbarContent = (
    <DDSTabs
      value={validSelectedSection}
      onValueChange={setSelectedSection}
      className={styles.devToolbar}
      data-name="dev_tools_tabs"
    >
      <div className={styles.devToolbarHeader}>
        <div className={styles.devToolbarHeaderTitle}>
          <DDSHeadlineText as="h2" level="md">
            ClassDojo Dev Toolbar
          </DDSHeadlineText>
        </div>
        <div className={styles.devToolbarHeaderActions}>
          {!isPoppedOut && <DockPositionPicker current={panelDisplaySide} onChange={setPanelDisplaySide} />}
          {!isPoppedOut && (
            <DDSButton
              variant="unstyled"
              data-name="dev_tools_popout"
              onClick={popOut}
              className={styles.closeButton}
              aria-label={"Pop out" as TranslatedString}
            >
              <IconOpenin size="s" />
            </DDSButton>
          )}
          {isPoppedOut && (
            <DDSButton
              variant="unstyled"
              data-name="dev_tools_popin"
              onClick={popIn}
              className={styles.closeButton}
              aria-label={"Dock panel" as TranslatedString}
            >
              <IconX size="m" />
            </DDSButton>
          )}
          {!isPoppedOut && (
            <DDSButton
              variant="unstyled"
              data-name="dev_tools_close"
              onClick={closeDevToolbar}
              className={styles.closeButton}
              aria-label={"Close dev tools" as TranslatedString}
            >
              <IconX size="m" />
            </DDSButton>
          )}
        </div>
      </div>
      <VersionInfo />
      <DDSTabsList className={styles.tabsContainer}>
        {keys(allSections).map((sectionKey) => (
          <DDSTabTrigger key={sectionKey} value={sectionKey} data-name={`dev_tools_tab_${sectionKey}`} size="medium">
            {allSections[sectionKey].title as TranslatedString}
          </DDSTabTrigger>
        ))}
      </DDSTabsList>
      {/* Wrap the active section in DDSTabContent so Radix can match the
          trigger's `aria-controls` to a real panel ID. Without this, the
          triggers reference a non-existent content node and axe's
          `aria-valid-attr-value` rule (correctly) fails. */}
      <DDSTabContent value={validSelectedSection} className={styles.contentContainer}>
        <DevToolsErrorBoundary key={validSelectedSection}>
          {createElement(allSections[validSelectedSection].component)}
        </DevToolsErrorBoundary>
      </DDSTabContent>
    </DDSTabs>
  );

  if (isPoppedOut) {
    return (
      <PopOutWindow onClose={popIn} title="ClassDojo Dev Toolbar">
        {toolbarContent}
      </PopOutWindow>
    );
  }

  return (
    <DevToolbarWrapper key={panelDisplaySide} isOpen={isOpen} panelDisplaySide={panelDisplaySide}>
      {toolbarContent}
    </DevToolbarWrapper>
  );
};

interface PopOutWindowProps {
  onClose: VoidFunction;
  title: string;
  children: React.ReactNode;
}

const PopOutWindow = ({ onClose, title, children }: PopOutWindowProps) => {
  const handleOpen = useCallback((childWindow: Window) => {
    const theme = document.documentElement.getAttribute("data-theme");
    if (theme) {
      childWindow.document.documentElement.setAttribute("data-theme", theme);
    }
  }, []);

  return (
    <NewWindow
      title={title}
      features={{ width: 620, height: 700 }}
      onUnload={onClose}
      onOpen={handleOpen}
      center="parent"
      copyStyles
    >
      {children}
    </NewWindow>
  );
};

interface DevToolbarWrapperProps {
  isOpen: boolean;
  panelDisplaySide: PanelDisplaySide;
}

const DEFAULT_SIZES: Record<PanelDisplaySide, number> = {
  left: 617,
  right: 617,
  bottom: 400,
};

function getSavedSize(position: PanelDisplaySide): number | null {
  const val = safeStorage.getItem(`devTools.panelSize.${position}`);
  return val ? Number(val) : null;
}

function saveSize(position: PanelDisplaySide, size: number) {
  safeStorage.setItem(`devTools.panelSize.${position}`, String(size));
}

const DevToolbarWrapper = ({ isOpen, panelDisplaySide, children }: React.PropsWithChildren<DevToolbarWrapperProps>) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isBottom = panelDisplaySide === PANEL_DISPLAY_SIDES.Bottom;
  const dockPosition = isBottom ? "bottom" : panelDisplaySide === PANEL_DISPLAY_SIDES.Left ? "left" : "right";
  const clampSize = useCallback(
    (raw: number) => {
      const min = isBottom ? 300 : 380;
      const max = isBottom ? window.innerHeight * 0.98 : window.innerWidth * 0.98;
      return Math.min(Math.max(raw, min), max);
    },
    [isBottom],
  );

  const [size, setSize] = useState(
    isMobile ? 1 : clampSize(getSavedSize(panelDisplaySide) ?? DEFAULT_SIZES[panelDisplaySide] ?? 617),
  );

  const debouncedSaveSize = useMemo(() => debounce(saveSize, 300), []);
  useEffect(() => () => debouncedSaveSize.cancel(), [debouncedSaveSize]);

  // Clamps the panel size when the window resizes. On mobile the panel is fluid
  // and never clamped, so the target is nullish and no listener is attached.
  useEventListener(
    "resize",
    () => {
      setSize((prev) => clampSize(prev));
    },
    isMobile ? null : globalThis.window,
  );

  return (
    <Dock
      position={dockPosition}
      fluid={isMobile}
      size={size}
      onSizeChange={
        isMobile
          ? undefined
          : (newSize) => {
              const clamped = clampSize(newSize);
              setSize(clamped);
              debouncedSaveSize(panelDisplaySide, clamped);
            }
      }
      isVisible={isOpen}
      zIndex={DOCK_Z_INDEX}
    >
      <div className={styles.dockContent}>{children}</div>
    </Dock>
  );
};

//
// Dev Toolbar metrics
//
const DEV_TOOLS_OPENED_METRIC = "web.devtools.opened";
const useIncrementDevToolsOpenedMetric = () => {
  const [hasSentMetric, setHasSentMetric] = useState(false);

  const sendMetric = useCallback(() => {
    if (!hasSentMetric) {
      setHasSentMetric(true);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      logClient.sendMetrics([
        {
          type: "increment",
          value: 1,
          metricName: DEV_TOOLS_OPENED_METRIC,
        },
      ]);
    }
  }, [hasSentMetric]);

  return sendMetric;
};
